import { ResponsiveLine } from '@nivo/line'
import { useSnapshot } from 'valtio'
import format from 'date-fns/format'
import { utcToZonedTime } from 'date-fns-tz'
import { type ReactElement } from 'react'
import { type KPISummaryType, recentSalesProxy } from '../recentSalesProxy'
import { usdFormatter } from '../../../../../../shared/utils/formattingUtils'
import { timeIntervalProxy, TimeIntervals } from '../../timeIntervalProxy'

interface dataArrayInnerObject {
  x: string | number
  y: string | number
  z?: string | number
}

export interface LineDataArrayObject {
  id: string | number
  color?: string | number
  data: dataArrayInnerObject[]
}

function formatMonthDay (v: string): string {
  const date = utcToZonedTime(v, 'UTC')
  return format(date, 'MM/dd')
}
export function parseGraphData (results: KPISummaryType[]): LineDataArrayObject[] {
  const d1: dataArrayInnerObject[] = []
  const d2: dataArrayInnerObject[] = []
  const salesObject = { color: 'hsl(164, 61%, 32%)', data: d1, id: 'Sales' }
  const refundObject = { color: 'hsl(0, 62%, 42%)', data: d2, id: 'Refunds' }

  results.forEach(value => {
    salesObject.data.push({
      x: formatMonthDay(value.endDate),
      y: String(value.salesDollarsCents),
      z: value.salesTxnsCount
    })

    refundObject.data.push({
      x: formatMonthDay(value.endDate),
      y: String(value.refundDollarsCents),
      z: value.refundTxnsCount
    })
  })

  salesObject.data = salesObject.data.reverse()
  refundObject.data = refundObject.data.reverse()

  return [refundObject, salesObject]
}

function chartTooltip (slice: any): ReactElement {
  return (
    slice.slice.points.map((v: any) => (
      <div key={v.serieId} className="p-6 m-6 text-white" style={{ backgroundColor: v.serieColor }}>
        <div>{v.serieId}</div>
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
        <div>Amount: {usdFormatter.format(v.data?.y)} </div>
        <div>Transactions: {v.data?.z} </div>
      </div>

    ))
  )
}

export function TransactionLineChart (): ReactElement {
  const { parsedGraphData } = useSnapshot(recentSalesProxy)
  const { timeInterval } = useSnapshot(timeIntervalProxy)

  if (!parsedGraphData || parsedGraphData.length === 0) {
    return (<></>)
  }

  const displayedtimeInterval = timeInterval === TimeIntervals.Weekly ? 'Weekly' : 'Monthly'

  return (
    <ResponsiveLine
      // @ts-expect-error
      data={parsedGraphData}
      margin={{ bottom: 50, left: 80, right: 80, top: 50 }}
      yFormat=" >-.2f"
      axisLeft={{
        format: value =>
                     `$${Number(value).toLocaleString('en-US', {
                         minimumFractionDigits: 0
                        })}`
      }}
      useMesh
      colors={{ datum: 'color' }}
      pointSize={6}
      pointColor={{ theme: 'background' }}
      pointBorderWidth={6}
      pointBorderColor={{ from: 'serieColor' }}
      pointLabelYOffset={-12}
      sliceTooltip={(slice) => chartTooltip(slice)}
      enableSlices="x"
      axisBottom={{
        legend: `${displayedtimeInterval} End Date`,
        legendOffset: 36,
        legendPosition: 'middle',
        tickPadding: 5,
        tickRotation: 0,
        tickSize: 5
      }}
      legends={[
        {
          anchor: 'bottom-right',
          direction: 'column',
          effects: [
            {
              on: 'hover',
              style: {
                itemBackground: 'rgba(0, 0, 0, .03)',
                itemOpacity: 1
              }
            }
          ],
          itemDirection: 'left-to-right',
          itemHeight: 20,
          itemOpacity: 0.75,
          itemsSpacing: 0,
          itemWidth: 80,
          justify: false,
          symbolBorderColor: 'rgba(0, 0, 0, .5)',
          symbolShape: 'circle',
          symbolSize: 12,
          translateX: 90,
          translateY: 0
        }
      ]}
    />
  )
}
